/* ============================================================
   mt-ab.js — A/B testing harness for the MT home page.
   Loaded synchronously in <head> so variants are resolved before paint.

   ## How variant assignment is decided (in order)
   1. **URL parameter** — e.g. `?ab_cta_price_v1=with_price`. Used by ad
      campaigns to force a specific variant. Sets the cookie too so the
      variant sticks for follow-up visits.
      Also supported: `?ab=cta_price_v1:with_price,other_exp:variant_b`.
   2. **Cookie** — `mt_ab_<experiment_id>`. Persists 30 days. Once a visitor
      is assigned a variant, they keep seeing the same one.
   3. **Random pool** — first-time visitor with no URL/cookie. Picks a
      variant per `weights`. Sets the cookie. From then on, treats the
      visitor like #2 above.

   ## How variants apply to the page
   - The variant value is merged into `window.MT_TWEAKS` so other code can read it.
   - DOM mutations happen via the APPLIERS map below. Add an applier per tweak.
   - A class `mt-ab-<expid>-<variant>` is set on <html> for CSS-driven swaps
     (e.g. `html.mt-ab-cta_price_v1-with_price .mt-btn__price { display: inline }`).

   ## Analytics — how a variant reaches a conversion (rebuilt 2026-07-30)
   Three sinks, in order of how much you should trust them:

   1. **Shopify cart attributes** ← the one that matters. Every assignment is
      written to the cart via `/cart/update.js`, so it rides onto the order as
      `note_attributes`. Shopify is the only system that sees every real order,
      so this is the canonical variant→purchase join. See syncCartAttributes().
   2. **GA4 user properties** — `mt_experiment_id` / `mt_variant_id` are set as
      USER-scoped properties, which is what makes them attach to `purchase`
      (fired later, on the checkout pages, in a different JS context). The
      event-scoped `mt_ab_exposure` event still fires too, for exposure counts.
   3. **Microsoft Clarity** — `clarity('set', 'mt_ab_<expid>', variant)`.
      Session-scoped custom tags, cross-tabbable against Clarity smart events
      in the Clarity dashboard only (the MCP does not return custom tags).

   Why this was rebuilt: `hero_copy_v1` ran Jul 3–30 2026 across ~3,900
   sessions and produced zero readable result. `variant_id` was event-scoped,
   so it only ever attached to `mt_ab_exposure` and never to a conversion, and
   the gtag retry below had an inverted guard that dropped the event entirely
   on ~95% of sessions. Shopify orders carried no variant at all. Full
   post-mortem in EXPERIMENT_LOG.md.

   ## Adding a new experiment
   1. Add an entry to EXPERIMENTS below (i.e. in mt-ab-variants.js).
   2. Mark it `primary: true` — that is what puts its variant into the GA4
      user properties you actually report on. Only one experiment can be
      primary; if none is flagged, the first experiment with 2+ non-zero
      weights is used, and if nothing is splitting, no user properties are set.
   3. If the tweak needs DOM mutation, add an applier in APPLIERS.
   4. (Optional) Add CSS that styles `html.mt-ab-<expid>-<variant>`.
   5. Push to Shopify, then log the experiment in EXPERIMENT_LOG.md.
   ============================================================ */
(function () {
  'use strict';

  // ============================================================
  // EXPERIMENT REGISTRY — sourced from assets/mt-ab-variants.js (loaded
  // BEFORE this script in <head>). Edit that file to add experiments,
  // change weights, or revise copy. Keeping data and logic in separate
  // files: mt-ab.js is stable infrastructure, mt-ab-variants.js churns
  // as themes/offers get tested.
  // ============================================================
  var EXPERIMENTS = (window && window.MT_AB_VARIANTS) ? window.MT_AB_VARIANTS : {};

  // ============================================================
  // Helpers
  // ============================================================
  var COOKIE_DAYS = 30;

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, days) {
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + ((days || COOKIE_DAYS) * 86400) + '; SameSite=Lax';
  }
  function pickWeighted(weights) {
    var total = 0, k;
    for (k in weights) total += weights[k];
    if (total <= 0) return Object.keys(weights)[0];
    var r = Math.random() * total;
    // a 0% arm is never picked, even when Math.random() returns exactly 0
    for (k in weights) { if (!(weights[k] > 0)) continue; r -= weights[k]; if (r <= 0) return k; }
    return Object.keys(weights)[0];
  }

  // ============================================================
  // A variant may split its visitors among sub-variants, each a variant of
  // its own: `split: { bundle_resonance_a: 34, bundle_resonance_b: 33, ... }`
  // on the parent. An ad link pins the parent (?ab_offer_v1=bundle_resonance);
  // the visitor lands on one sub-variant, chosen once by weight and kept in the
  // cookie, so she sees the same page next time. The sub-variant's name is
  // what the cart, GA4 and Clarity receive (added 2026-09-30 for the Stg 7
  // headline test).
  // ============================================================
  function splitOf(exp, variant) {
    var def = exp.variants[variant];
    return def && def.split && typeof def.split === 'object' ? def.split : null;
  }
  function settle(expId, exp, variant, source) {
    var split = splitOf(exp, variant);
    if (!split) { setCookie('mt_ab_' + expId, variant); return { variant: variant, source: source }; }
    var ck = getCookie('mt_ab_' + expId);
    if (ck && split.hasOwnProperty(ck) && exp.variants.hasOwnProperty(ck)) return { variant: ck, source: 'cookie' };
    var chosen = pickWeighted(split);
    if (!exp.variants.hasOwnProperty(chosen)) { setCookie('mt_ab_' + expId, variant); return { variant: variant, source: source }; }
    setCookie('mt_ab_' + expId, chosen);
    return { variant: chosen, source: 'split' };
  }

  // ============================================================
  // Resolve a single experiment to its variant assignment.
  // Returns { variant, source }.
  // ============================================================
  function resolve(expId, exp) {
    var params = new URLSearchParams(location.search);

    // 1. Per-experiment URL param: ?ab_<expid>=<variant>
    var urlVariant = params.get('ab_' + expId);
    if (urlVariant && exp.variants.hasOwnProperty(urlVariant)) {
      return settle(expId, exp, urlVariant, 'url');
    }

    // 2. Combined ?ab=<expid>:<variant>[,<expid>:<variant>] form
    var combined = params.get('ab');
    if (combined) {
      var pairs = combined.split(',');
      for (var i = 0; i < pairs.length; i++) {
        var parts = pairs[i].split(':');
        if (parts[0] === expId && parts[1] && exp.variants.hasOwnProperty(parts[1])) {
          return settle(expId, exp, parts[1], 'url');
        }
      }
    }

    // 3. Existing cookie (a cookie that still names a parent with a split is settled to one of its sub-variants)
    var ck = getCookie('mt_ab_' + expId);
    if (ck && exp.variants.hasOwnProperty(ck)) {
      return splitOf(exp, ck) ? settle(expId, exp, ck, 'cookie') : { variant: ck, source: 'cookie' };
    }

    // 4. Random assignment from weighted pool
    var chosen = pickWeighted(exp.weights);
    return settle(expId, exp, chosen, 'random');
  }

  // ============================================================
  // Resolve all experiments. Run synchronously so CSS classes are set
  // BEFORE the body renders (no FOUC for class-driven variants).
  //
  // Two passes:
  //   1. Resolve every experiment to a variant + set its <html> class.
  //   2. Walk the resolved variants again and merge tweakOverrides, with
  //      full assignment context available — so a variant's `themeText`
  //      can look up the resolved theme_v1 variant and apply the right
  //      theme-keyed copy.
  //
  // Override key semantics:
  //   text         { keyA: '...', keyB: '...' }
  //                  → setTextContent on every [data-mt-text=key].
  //   images       { keyA: '/path.jpg' }
  //                  → set src / background-image on every [data-mt-image=key].
  //   themeText    { occult_belief: { keyA: '...' }, meta_therapy: { ... } }
  //                  → looks up assignments.theme_v1.variant, applies the
  //                    matching map. Used by headline_v1 alts whose copy
  //                    is theme-specific (so each (theme × alt) cell has
  //                    its own purpose-fit H1 / pre-CTA / etc.).
  //   themeImages  { occult_belief: { keyA: '/...' }, meta_therapy: {...} }
  //                  → same pattern, image swaps.
  //   text + themeText for the same key: themeText wins (it's the more
  //   specific override).
  //
  // text/images deep-merge across experiments (later experiments win on key
  // conflicts). Legacy flat tweaks (ctaPriceInline, etc.) shallow-overwrite.
  // ============================================================
  var assignments = {};
  var tweakOverrides = {};
  var hasOverrides = false;

  // Pass 1: resolve all experiments to variants
  for (var expId in EXPERIMENTS) {
    var exp = EXPERIMENTS[expId];
    if (!exp || !exp.variants) continue;
    var res = resolve(expId, exp);
    assignments[expId] = res;

    // <html> class used for CSS-driven variants
    if (document.documentElement) {
      document.documentElement.classList.add('mt-ab-' + expId + '-' + res.variant);
    }
  }

  // Pass 2: merge tweakOverrides, with full assignments visible (so themeText
  // / themeImages can resolve against the active theme_v1 variant).
  var activeThemeVariant = (assignments.theme_v1 || {}).variant || null;

  for (var expId2 in EXPERIMENTS) {
    var exp2 = EXPERIMENTS[expId2];
    if (!exp2 || !exp2.variants || !assignments[expId2]) continue;
    var variantTweaks = exp2.variants[assignments[expId2].variant] || {};

    for (var t in variantTweaks) {
      if (t === 'text' || t === 'images') {
        tweakOverrides[t] = tweakOverrides[t] || {};
        for (var k in variantTweaks[t]) {
          tweakOverrides[t][k] = variantTweaks[t][k];
          hasOverrides = true;
        }
      } else if (t === 'themeText' || t === 'themeImages') {
        // Theme-keyed: look up the right map based on the resolved theme_v1.
        // No-op if theme_v1 isn't running, resolved to default, or this alt
        // doesn't have copy for the active theme — preserves graceful
        // fallback to the theme's own base copy.
        if (!activeThemeVariant || !variantTweaks[t][activeThemeVariant]) continue;
        var targetKey = (t === 'themeText') ? 'text' : 'images';
        tweakOverrides[targetKey] = tweakOverrides[targetKey] || {};
        var themedMap = variantTweaks[t][activeThemeVariant];
        for (var kk in themedMap) {
          tweakOverrides[targetKey][kk] = themedMap[kk];
          hasOverrides = true;
        }
      } else {
        tweakOverrides[t] = variantTweaks[t];
        hasOverrides = true;
      }
    }
  }

  // Anti-FOUC: hide swap-target elements until apply() runs. Only added
  // when there's something to swap — organic / default-only traffic gets
  // no class added and no flash. CSS rule lives in mt-v2-head.liquid.
  if (hasOverrides && document.documentElement) {
    document.documentElement.classList.add('mt-ab-applying');
  }

  window.MT_AB_ASSIGNMENTS = assignments;
  window.MT_AB_OVERRIDES = tweakOverrides;

  // ============================================================
  // UTM capture for Clarity tagging.
  // UTMs land on the FIRST hit of a session (e.g. an ad click). We persist
  // them in sessionStorage so Clarity replays of deeper pages still get
  // tagged with the originating ad. GA4 already auto-attributes UTMs to
  // events, so we don't push them to the dataLayer.
  // ============================================================
  function captureUtms() {
    var params = new URLSearchParams(location.search);
    var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
    var found = {};
    var any = false;
    for (var i = 0; i < keys.length; i++) {
      var v = params.get(keys[i]);
      if (v) { found[keys[i]] = v; any = true; }
    }
    if (any) {
      try { sessionStorage.setItem('mt_utm', JSON.stringify(found)); } catch (e) {}
      return found;
    }
    try {
      var stored = sessionStorage.getItem('mt_utm');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  }
  var utms = captureUtms();

  // ============================================================
  // Cart attributes — the variant → order join.
  //
  // GA4 can tell us how many SESSIONS a variant got. Only Shopify knows what
  // the BUYER saw. Cart attributes ride onto the order as `note_attributes`,
  // so every order carries its own assignment and its own ad.
  //
  // Keys written (stable contract — documented in EXPERIMENT_LOG.md and the
  // analytics-stack skill; read by any order-side analysis):
  //   mt_ab_<experiment_id>   resolved variant, one key per running experiment
  //   mt_utm_content          ad name — Shopify truncates landing_site at 255
  //                           chars and long campaign names blow past it
  //   mt_fbclid               Meta click id — dropped by that same truncation,
  //                           which is what forced the CAPI backfill script
  //
  // When it fires:
  //   - after a successful /cart/add.js (mt-home.js calls this)
  //   - on page load IF a cart already exists (`cart` cookie present), so a
  //     cart that was abandoned before this shipped still picks the data up
  // It deliberately does NOT fire on a first page load with no cart — that
  // would create an empty cart record for every visitor just to hold tracking
  // data.
  //
  // It must never cost a sale: fire-and-forget (never awaited in the
  // add-to-cart path), `keepalive` so a buy-now navigation can't cancel it,
  // one retry for the 429 Shopify throws on back-to-back cart writes, and
  // every failure swallowed.
  // ============================================================
  var CART_ATTR_MEMO = 'mt_cart_attrs';

  function buildCartAttributes() {
    var attrs = {};
    for (var expId in assignments) {
      attrs['mt_ab_' + expId] = assignments[expId].variant;
    }
    if (utms) {
      if (utms.utm_content) attrs.mt_utm_content = utms.utm_content;
      if (utms.fbclid) attrs.mt_fbclid = utms.fbclid;
    }
    return attrs;
  }

  // Memo is keyed on cart token + payload, not payload alone: if the cart
  // expires and Shopify issues a new token, the same payload has to be
  // written again onto the new cart.
  function memoValue(token, payload) { return (token || '') + '|' + payload; }

  function syncCartAttributes(attempt) {
    var attrs = buildCartAttributes();
    var payload;
    try { payload = JSON.stringify(attrs); } catch (e) { return; }
    if (payload === '{}') return;

    if (!attempt) {
      try {
        if (sessionStorage.getItem(CART_ATTR_MEMO) === memoValue(getCookie('cart'), payload)) return;
      } catch (e) {}
    }

    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true
      }).then(function (r) {
        if (r && r.ok) {
          return r.json().then(function (cart) {
            try {
              sessionStorage.setItem(CART_ATTR_MEMO, memoValue(cart && cart.token, payload));
            } catch (e) {}
          }).catch(function () {});
        }
        // Shopify 429s / 503s on back-to-back cart writes. One retry, then
        // give up quietly — a missing attribute is not worth a second failure.
        if (!attempt) {
          setTimeout(function () { syncCartAttributes(1); }, 700);
        }
      }).catch(function () {
        if (!attempt) {
          setTimeout(function () { syncCartAttributes(1); }, 700);
        }
      });
    } catch (e) {}
  }

  // ============================================================
  // APPLIERS — run after DOM is parsed. Each function takes the resolved
  // value and mutates the page accordingly. Keep them idempotent.
  // ============================================================
  var APPLIERS = {
    // ----------------------------------------------------------
    // Text swap: {key: "new copy"} → set textContent on every
    // <* data-mt-text="key">. Idempotent. The CSS in mt-v2-head.liquid
    // hides these elements while mt-ab-applying is on <html>.
    // ----------------------------------------------------------
    text: function (textMap) {
      if (!textMap) return;
      for (var key in textMap) {
        // Escape double-quotes so the attribute selector is valid for any key.
        var sel = '[data-mt-text="' + key.replace(/"/g, '\\"') + '"]';
        var els = document.querySelectorAll(sel);
        for (var i = 0; i < els.length; i++) {
          els[i].textContent = textMap[key];
        }
      }
    },
    // ----------------------------------------------------------
    // Image swap: {key: "/path.jpg"} → set src (or background-image)
    // on every <* data-mt-image="key">. For <img>, also strips srcset
    // so the new src isn't out-bid by responsive variants.
    // ----------------------------------------------------------
    images: function (imageMap) {
      if (!imageMap) return;
      for (var key in imageMap) {
        var sel = '[data-mt-image="' + key.replace(/"/g, '\\"') + '"]';
        var els = document.querySelectorAll(sel);
        for (var i = 0; i < els.length; i++) {
          var el = els[i];
          if (el.tagName === 'IMG') {
            if (el.hasAttribute('srcset')) el.removeAttribute('srcset');
            el.src = imageMap[key];
          } else {
            el.style.backgroundImage = 'url("' + String(imageMap[key]).replace(/"/g, '\\"') + '")';
          }
        }
      }
    },
    // CTA price inline: add or remove " — $XX.XX" from button text.
    ctaPriceInline: function (showPrice) {
      // Prices are DERIVED from window.MT_DATA.bundles (snippets/mt-data.liquid),
      // which computes them from the live Shopify variants.
      //
      // This was a hardcoded map of '$54.99' / '$29.99' literals. Those went
      // stale at the May 2026 reprice and stayed wrong through 2026-09-03 —
      // undetected only because the ctaPriceInline knob happens to be false,
      // so nothing rendered them. Flipping that knob would have printed
      // months-old prices onto every add-to-cart button.
      var sym = (window.MT_CURRENCY && window.MT_CURRENCY.symbol) || '$';
      var bundles = (window.MT_DATA && window.MT_DATA.bundles) || {};
      var PRICES = {};
      Object.keys(bundles).forEach(function (k) {
        var b = bundles[k];
        if (!b || !b.variantId || typeof b.price !== 'number') return;
        PRICES[String(b.variantId)] = sym + b.price.toFixed(2);
      });
      document.querySelectorAll('[data-variant-id]').forEach(function (el) {
        if (el.matches('input, select, .mt-cart__row *, .mt-cart__upsell *')) return; // skip cart internals
        var vid = el.getAttribute('data-variant-id');
        var price = PRICES[vid];
        if (!price) return;
        var raw = (el.textContent || '').replace(/\s+/g, ' ').trim();
        // strip any existing " — $XX.XX"
        // Currency-agnostic: Shopify Markets serves €/£ to non-US visitors, and
        // a $-only pattern would fail to strip an existing price before
        // re-appending, doubling it up.
        var stripped = raw.replace(/\s*—\s*[$€£]\s?\d+([.,]\d+)?\s*/, ' ').replace(/\s+/g, ' ').trim();
        if (showPrice) {
          var arrowMatch = stripped.match(/^(.*?)([→↗➜])\s*$/);
          if (arrowMatch) {
            el.textContent = arrowMatch[1].trim() + ' — ' + price + ' ' + arrowMatch[2];
          } else {
            el.textContent = stripped + ' — ' + price;
          }
        } else {
          el.textContent = stripped;
        }
      });
    }
    // Add more appliers here as new experiments are added.
  };

  // ============================================================
  // Apply overrides + fire analytics
  // ============================================================
  function apply() {
    // Merge into MT_TWEAKS so other scripts read the variant value
    window.MT_TWEAKS = window.MT_TWEAKS || {};
    for (var k in tweakOverrides) window.MT_TWEAKS[k] = tweakOverrides[k];

    // Run appliers
    for (var t in tweakOverrides) {
      if (typeof APPLIERS[t] === 'function') {
        try { APPLIERS[t](tweakOverrides[t]); }
        catch (e) { console.error('[mt-ab] applier failed for ' + t, e); }
      }
    }

    // Anti-FOUC: reveal swap-target elements now that appliers ran.
    if (document.documentElement) {
      document.documentElement.classList.remove('mt-ab-applying');
    }

    fireAnalytics();

    // Stamp an EXISTING cart with the current assignment (see
    // syncCartAttributes). No cart cookie means no cart yet — leave it alone
    // and let the add-to-cart path create it.
    if (getCookie('cart')) syncCartAttributes();
  }

  // ------------------------------------------------------------
  // Which experiment's variant goes into the GA4 user properties.
  // GA4 caps a user property VALUE at 36 chars, so we report one experiment
  // at a time rather than cramming every assignment into one string.
  //   1. an experiment flagged `primary: true` in the registry
  //   2. else the first experiment actually splitting traffic (2+ non-zero weights)
  //   3. else none — nothing is being tested, so there is nothing to report
  // ------------------------------------------------------------
  function resolvePrimaryExperiment() {
    var splitting = null;
    for (var expId in EXPERIMENTS) {
      var exp = EXPERIMENTS[expId];
      if (!exp || !assignments[expId]) continue;
      if (exp.primary) return expId;
      if (!splitting) {
        var live = 0;
        for (var v in (exp.weights || {})) { if (exp.weights[v] > 0) live++; }
        if (live >= 2) splitting = expId;
      }
    }
    return splitting;
  }
  var PRIMARY_EXPERIMENT = resolvePrimaryExperiment();

  function fireAnalytics() {
    // ----------------------------------------------------------
    // GA4.
    //
    // The dataLayer push is for any GTM container that's listening. It is NOT
    // what feeds GA4 — the store runs a gtag Google tag (GT-…) with no GTM
    // container, and gtag ignores plain {event: …} objects. GA4 only sees the
    // direct gtag() calls below.
    //
    // Timing (this is what broke Jul 2026): the Google tag is injected by the
    // Google & YouTube channel AFTER our DOMContentLoaded, so `window.gtag` is
    // routinely undefined when this runs — verified live, our pushes land at
    // dataLayer[0..3] and gtag's own `config` at [11]. So we wait. We also
    // wait for gtag's `js`/`config` entry rather than just for the function to
    // exist: gtag's pre-library stub queues calls, but an event queued ahead of
    // the tag's own config is dropped.
    //
    // user_properties, not event params: an event-scoped param only attaches
    // to the event that carried it, which is why `variant_id` never reached a
    // purchase. User-scoped properties attach to the user, so GA4 associates
    // them with the `purchase` fired later from the checkout pages.
    // ----------------------------------------------------------
    window.dataLayer = window.dataLayer || [];
    for (var expId in assignments) {
      window.dataLayer.push({
        event: 'mt_ab_exposure',
        experiment_id: expId,
        variant_id: assignments[expId].variant,
        assignment_source: assignments[expId].source
      });
    }

    function gtagReady() {
      if (typeof window.gtag !== 'function') return false;
      var dl = window.dataLayer || [];
      for (var i = 0; i < dl.length; i++) {
        var item = dl[i];
        // gtag pushes its own calls as `arguments` objects: array-like, not arrays.
        if (item && typeof item.length === 'number' && !Array.isArray(item) &&
            (item[0] === 'config' || item[0] === 'js')) return true;
      }
      return false;
    }

    function sendGa() {
      if (PRIMARY_EXPERIMENT) {
        try {
          window.gtag('set', 'user_properties', {
            mt_experiment_id: PRIMARY_EXPERIMENT,
            mt_variant_id: assignments[PRIMARY_EXPERIMENT].variant
          });
        } catch (e) {}
      }
      for (var id in assignments) {
        try {
          window.gtag('event', 'mt_ab_exposure', {
            experiment_id: id,
            variant_id: assignments[id].variant,
            assignment_source: assignments[id].source
          });
        } catch (e) {}
      }
    }

    if (gtagReady()) {
      sendGa();
    } else {
      var gaTries = 0;
      var gaIv = setInterval(function () {
        if (gtagReady()) {
          sendGa();
          clearInterval(gaIv);
        } else if (++gaTries > 80) {   // 20s — paid mobile can be slow to boot the tag
          clearInterval(gaIv);
        }
      }, 250);
    }

    // Microsoft Clarity custom tags. Clarity can load late, so retry briefly.
    // Internal-traffic guard: if we're on the local preview proxy
    // (`http://127.0.0.1:9295` or localhost), tag the session and call
    // `clarity('stop')` so the recording isn't uploaded — Scott's editing
    // sessions were polluting the production Clarity dataset (CA1).
    function isInternalPreview() {
      try {
        var h = location.hostname;
        return h === '127.0.0.1' || h === 'localhost' || location.port === '9295';
      } catch (e) { return false; }
    }
    function tagClarity() {
      if (typeof window.clarity !== 'function') return false;
      if (isInternalPreview()) {
        try { window.clarity('set', 'mt_internal', 'preview'); } catch (e) {}
        try { window.clarity('stop'); } catch (e) {}
        return true; // signal "done" so we don't keep retrying
      }
      for (var expId in assignments) {
        try { window.clarity('set', 'mt_ab_' + expId, assignments[expId].variant); }
        catch (e) {}
      }
      if (utms) {
        for (var k in utms) {
          try { window.clarity('set', k, utms[k]); }
          catch (e) {}
        }
      }
      return true;
    }
    if (!tagClarity()) {
      var tries = 0;
      var iv = setInterval(function () {
        if (tagClarity() || ++tries > 40) clearInterval(iv);
      }, 250);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }

  // Public surface for debugging / mt-home.js
  window.MT_AB = {
    EXPERIMENTS: EXPERIMENTS,
    assignments: assignments,
    overrides: tweakOverrides,
    utms: utms,
    // The experiment whose variant is reported to GA4 as a user property.
    primaryExperiment: PRIMARY_EXPERIMENT,
    // What will be written onto the cart / order. `MT_AB.cartAttributes()`
    // from devtools shows exactly what should land in note_attributes.
    cartAttributes: buildCartAttributes,
    // Called by mt-home.js after a successful /cart/add.js. Safe to call by
    // hand; it no-ops when the cart already carries these values.
    syncCartAttributes: syncCartAttributes,
    // Helper to clear cookie state — `MT_AB.reset()` from devtools
    reset: function () {
      for (var expId in EXPERIMENTS) {
        document.cookie = 'mt_ab_' + expId + '=; path=/; max-age=0';
      }
      try { sessionStorage.removeItem('mt_utm'); } catch (e) {}
      try { sessionStorage.removeItem(CART_ATTR_MEMO); } catch (e) {}
      console.log('[mt-ab] all experiment cookies + UTM session cleared. Reload to re-randomize.');
    }
  };
})();
